import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, ShieldCheck, CheckCircle2, ArrowLeft, Calendar } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import usePageMeta from "../lib/usePageMeta";

const URGENCY_OPTIONS = [
  { value: "routine", label: "Routine — within a few days is fine" },
  { value: "soon", label: "Soon — within 24–48 hours please" },
  { value: "urgent", label: "Urgent — today if possible" },
  { value: "crisis", label: "Crisis — something serious has happened" },
];

const initialState = {
  first_name: "",
  last_name: "",
  company_name: "",
  phone: "",
  email: "",
  preferred_contact: "phone",
  best_time_to_contact: "",
  reason: "",
  urgency: "routine",
  consent: false,
};

export default function RequestSupportPage() {
  usePageMeta({
    title: "Request Support | CCCHAPS Care Portal",
    description: "Confidential support request form for employees of organizations enrolled with California Corporate Chaplains.",
    noindex: true,
  });
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [scheduleUrl, setScheduleUrl] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config").then((r) => setScheduleUrl(r.data?.schedule_chaplain_url || "")).catch(() => {});
  }, []);
  const scheduleTarget = scheduleUrl || "/care-portal/request-support";
  const scheduleExternal = Boolean(scheduleUrl);

  const update = (k) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [k]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please confirm consent to submit your request.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/support-requests", form);
      setSubmitted(res.data);
      toast.success("Request submitted. A chaplain will reach out shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24" data-testid="request-success">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 md:p-12 text-center shadow-sm">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-50 text-green-600 mb-6">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">You've been heard.</h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Your request has been received confidentially. A chaplain will contact you using your preferred method.
            Employers receive only aggregate, non-identifying utilization reports. Chaplain conversations are
            confidential within applicable ethical and legal limits.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={() => navigate("/")}
              data-testid="success-back-home"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </button>
            <button
              onClick={() => navigate("/care-portal/resources")}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors"
            >
              Browse resources
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" data-testid="request-support-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium mb-3">Request support</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">
          Tell us how to reach you.
        </h1>
        <p className="mt-3 text-slate-600 leading-relaxed">
          A chaplain will follow up using your preferred method. What you share is handled confidentially within applicable ethical and legal limits.
        </p>
      </div>

      <div className="rounded-xl border border-blue-100 bg-[#F0F4F8] p-4 mb-8 flex gap-3" data-testid="form-confidentiality-banner">
        <Lock className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 leading-relaxed space-y-1.5">
          <p>
            Information submitted through this portal is used to connect you with chaplaincy support. CCCHAPS documents service activity, not the private content of your conversations. Employers receive aggregate, non-identifying utilization reports showing categories such as visits, phone calls, personal support meetings, and prayer or spiritual support. Routine reports do not include employee names or written accounts of confidential conversations. If additional professional assistance is needed, a chaplain may recommend your organization's EAP or another appropriate resource.
          </p>
          <p className="text-xs text-slate-600" data-testid="form-confidentiality-limits-note">
            Confidentiality is maintained within applicable ethical and legal limits, including legally required disclosures and serious safety concerns.
          </p>
          <p className="text-xs text-slate-600">
            A chaplain follows up using your preferred contact method. This form is not a live-chat channel; for direct phone support, use CALLACHAP.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 space-y-6 shadow-sm" data-testid="support-request-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="First name" required>
            <input data-testid="input-first-name" required value={form.first_name} onChange={update("first_name")} className={inputCls} />
          </Field>
          <Field label="Last name" required>
            <input data-testid="input-last-name" required value={form.last_name} onChange={update("last_name")} className={inputCls} />
          </Field>
        </div>

        <Field label="Company name" required>
          <input data-testid="input-company" required value={form.company_name} onChange={update("company_name")} className={inputCls} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Phone number" required>
            <input data-testid="input-phone" required type="tel" value={form.phone} onChange={update("phone")} className={inputCls} />
          </Field>
          <Field label="Email" required>
            <input data-testid="input-email" required type="email" value={form.email} onChange={update("email")} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Preferred contact method" required>
            <select data-testid="select-preferred-contact" value={form.preferred_contact} onChange={update("preferred_contact")} className={inputCls}>
              <option value="phone">Phone call</option>
              <option value="text">Text message</option>
              <option value="email">Email</option>
            </select>
          </Field>
          <Field label="Best time to contact" required>
            <input data-testid="input-best-time" required value={form.best_time_to_contact} onChange={update("best_time_to_contact")} placeholder="e.g. Weekdays after 4 PM PT" className={inputCls} />
          </Field>
        </div>

        <Field label="General reason for support" required>
          <textarea data-testid="textarea-reason" required value={form.reason} onChange={update("reason")} rows={4} placeholder="A few sentences are enough — only as much as you'd like to share right now." className={`${inputCls} resize-none`} />
        </Field>

        <Field label="Urgency level" required>
          <select data-testid="select-urgency" value={form.urgency} onChange={update("urgency")} className={inputCls}>
            {URGENCY_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
        </Field>

        <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="consent-checkbox-wrapper">
          <input data-testid="checkbox-consent" type="checkbox" checked={form.consent} onChange={update("consent")} className="mt-1 w-4 h-4 rounded border-slate-300 text-[#4A6FA5] focus:ring-[#4A6FA5]" />
          <span className="text-sm text-slate-700 leading-relaxed">
            I understand this is a confidential request for chaplaincy support and consent to a chaplain contacting me using the method I selected. I understand this portal is not for medical or mental health emergencies.
          </span>
        </label>

        <div className="pt-2 flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={submitting || !form.consent}
            data-testid="submit-request-button"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? "Submitting..." : "Submit Confidential Request"}
          </button>
          <a
            href={scheduleTarget}
            target={scheduleExternal ? "_blank" : undefined}
            rel={scheduleExternal ? "noreferrer" : undefined}
            data-testid="form-schedule-btn"
            className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-700 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] transition-colors"
          >
            <Calendar className="w-4 h-4" /> Or schedule directly
          </a>
        </div>

        <p className="text-xs text-slate-500 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5 text-[#4A6FA5]" /> Encrypted in transit. Stored confidentially.
        </p>
      </form>
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-transparent transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 mb-1.5">
        {label} {required && <span className="text-[#4A6FA5]">*</span>}
      </span>
      {children}
    </label>
  );
}
